import { Segmented } from "@/components/ui";
import { useT } from "@/i18n/LocaleProvider";
import { useTheme } from "./ThemeProvider";
import { SCHEMES, THEMES } from "./themes";

/**
 * Where somebody chooses how the interface looks: light or dark (system,
 * pinned either way), and which of the five colour themes.
 *
 * Each theme option is drawn *in its own theme*: the swatch carries
 * `data-theme`, so the colours it shows are the real tokens — the page, a
 * panel, the accent and the chart ramp — in whichever of light or dark the
 * chosen scheme resolves to. A preview cannot drift from what choosing it
 * does, because it is the same CSS.
 *
 * The theme is a radio group, because that is what it is: one of five, always
 * one chosen. The scheme is a segmented control: one of three.
 */
export function ThemePicker() {
  const t = useT();
  const { theme, setTheme, scheme, setScheme } = useTheme();

  return (
    <div>
      <Segmented
        legend={t("scheme.label")}
        name="scheme"
        value={scheme}
        onChange={setScheme}
        options={SCHEMES.map((option) => ({ value: option, label: t(`scheme.${option}`) }))}
      />
      {/* The hint is about the switch above it, not about the colour schemes. */}
      <p className="mt-1.5 text-xs text-muted">{t("theme.hint")}</p>
      <fieldset className="mt-5">
        <legend className="text-sm font-medium text-ink">{t("theme.label")}</legend>
        <div className="mt-3 grid grid-cols-1 gap-2">
          {THEMES.map((option) => {
            const checked = option === theme;
            return (
              <label
                key={option}
                className={`flex min-h-11 cursor-pointer items-center gap-3 rounded-control border px-2.5 py-2 transition-colors ${
                  checked
                    ? "border-accent bg-accent-soft"
                    : "border-line hover:bg-sunken"
                }`}
              >
                <input
                  type="radio"
                  name="theme"
                  value={option}
                  checked={checked}
                  onChange={() => setTheme(option)}
                  className="sr-only"
                  data-testid={`theme-${option}`}
                />
                <span
                  data-theme={option}
                  aria-hidden="true"
                  className="flex h-8 w-12 shrink-0 overflow-hidden rounded-md border border-line bg-canvas"
                >
                  <span className="m-1 flex flex-1 flex-col justify-between rounded-sm bg-surface p-0.5">
                    <span className="h-1.5 w-full rounded-full bg-accent" />
                    <span className="flex gap-px">
                      <span className="h-1.5 flex-1 bg-share-1" />
                      <span className="h-1.5 flex-1 bg-share-2" />
                      <span className="h-1.5 flex-1 bg-share-3" />
                      <span className="h-1.5 flex-1 bg-share-4" />
                    </span>
                  </span>
                </span>
                <span className="min-w-0">
                  <span className="block text-sm font-medium text-ink">{t(`theme.${option}`)}</span>
                  <span className="block text-xs text-muted">{t(`theme.${option}.note`)}</span>
                </span>
                {checked && (
                  <svg
                    viewBox="0 0 16 16"
                    aria-hidden="true"
                    className="ml-auto h-4 w-4 shrink-0 text-accent-text"
                  >
                    <path
                      d="M3 8.5l3.2 3L13 4.5"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
              </label>
            );
          })}
        </div>
      </fieldset>
    </div>
  );
}
